import { useEffect } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Reusable case-study FAQ block with embedded FAQPage Schema.org JSON-LD.
 *
 * Two jobs:
 *   1. Render an accessible, accordion-style FAQ that buyers can skim
 *   2. Inject FAQPage schema so Google AI Overview, Perplexity, and ChatGPT
 *      search can cite individual Q&A pairs directly
 *
 * The schema is appended/removed on mount/unmount so each case study only
 * exposes its own FAQ schema while that page is mounted.
 *
 * Props:
 *   - testid: data-testid prefix
 *   - items: [{ q: string, a: string }, ...]   // 3-6 pairs recommended
 *   - schemaId: unique ID for the <script> tag (to support multiple FAQs)
 */
export default function CaseStudyFaq({ testid, items, schemaId }) {
  useEffect(() => {
    if (!items || items.length === 0) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = `faq-schema-${schemaId}`;
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => {
      const existing = document.getElementById(`faq-schema-${schemaId}`);
      if (existing) existing.remove();
    };
  }, [items, schemaId]);

  if (!items || items.length === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20"
      data-testid={testid}
    >
      <div className="label-mono">Questions buyers ask about this engagement</div>
      <h2 className="font-display text-2xl lg:text-3xl mt-3 font-semibold tracking-tight">
        Frequently asked questions
      </h2>
      <div className="mt-6 space-y-2">
        {items.map((item, idx) => (
          <details
            key={idx}
            className="group rounded-xl border border-border bg-white px-5 py-4 open:bg-[#f6f1fb]/30 transition-colors"
            data-testid={`${testid}-item-${idx}`}
          >
            <summary className="cursor-pointer list-none flex items-start justify-between gap-3">
              <span className="font-display text-base lg:text-lg font-semibold text-foreground leading-snug">
                {item.q}
              </span>
              <ChevronDown className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0 transition-transform group-open:rotate-180" aria-hidden />
            </summary>
            <p className="mt-3 text-sm lg:text-base text-foreground/80 leading-relaxed">
              {item.a}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}
